/**
 * Input recording and local replay inspection. A recording is the per-tick
 * InputFrame stream plus the spawn and configuration it started from. Because
 * the simulation is deterministic, replaying the inputs from the same spawn
 * with the same configuration reproduces the run exactly; `finalHash`
 * lets a replay confirm that it actually did.
 *
 * Recordings start from `Simulation.placeAtSpawn`, which resets the attempt and
 * all transient movement state, so recording and replay share an identical
 * initial state. The absolute tick counter is excluded from the hash; rule
 * state only ever uses tick differences.
 */
import type { InputFrame } from '../input/InputFrame';
import { MAX_SPAWN_COORD, type Simulation } from '../simulation/Simulation';
import type { SpawnPoint } from '../world/LevelData';

/** [moveX, moveZ, yaw, pitch, flags]; flags bit 0 = jumpHeld, bit 1 = jumpPressed, bit 2 = crouchHeld. */
export type PackedInput = [number, number, number, number, number];

export interface Recording {
  version: 1;
  levelId: string;
  recordKey: string;
  spawn: SpawnPoint;
  inputs: PackedInput[];
  finalHash: string;
}

export class RecordingError extends Error {}

/** 120 s at 120 Hz. */
export const MAX_RECORDING_TICKS = 120 * 120;

export function packInput(i: Readonly<InputFrame>): PackedInput {
  return [i.moveX, i.moveZ, i.yaw, i.pitch, (i.jumpHeld ? 1 : 0) | (i.jumpPressed ? 2 : 0) | (i.crouchHeld ? 4 : 0)];
}

export function unpackInput(p: Readonly<PackedInput>): InputFrame {
  return { moveX: p[0], moveZ: p[1], yaw: p[2], pitch: p[3], jumpHeld: (p[4] & 1) !== 0, jumpPressed: (p[4] & 2) !== 0, crouchHeld: (p[4] & 4) !== 0 };
}

/**
 * 64-bit fingerprint (two FNV-1a lanes over the exact float bits) of the
 * movement-relevant player state: kinematics, ground state, jump timers, alive
 * flag and rule-state values. The absolute tick counter is excluded; a rule
 * snapshot's trailing absolute tick (e.g. compression impactTick) is hashed as
 * an offset from the current tick. Non-finite values hash distinctly. Signed
 * zeros hash as +0: −0 and +0 are the same physical state, and JSON (how
 * recordings are saved) cannot carry the sign, so a −0 yaw input made an
 * identical replay look divergent. It detects divergence reliably; it is not
 * a cryptographic proof.
 */
export function stateHash(sim: Simulation): string {
  const s = sim.state;
  const values: number[] = [s.position.x, s.position.y, s.position.z, s.velocity.x, s.velocity.y, s.velocity.z, s.yaw, s.grounded ? 1 : 0, s.alive ? 1 : 0, s.jumpBuffer, Math.min(s.ticksSinceGrounded, 1e6), Math.min(s.ticksSinceJump, 1e6), ...s.techniqueValues()];
  for (const key of [...s.ruleState.keys()].sort()) {
    const snap = s.ruleState.get(key)!.snapshot();
    for (let i = 0; i < snap.length; i++) values.push(i === snap.length - 1 && snap[i]! >= 0 && Number.isInteger(snap[i]) ? s.tick - snap[i]! : snap[i]!);
  }
  const bits = new DataView(new ArrayBuffer(8));
  let a = 0x811c9dc5;
  let b = 0x9e3779b9;
  for (const v of values) {
    bits.setFloat64(0, v === 0 ? 0 : v);
    for (let i = 0; i < 8; i++) {
      const byte = bits.getUint8(i);
      a = Math.imul(a ^ byte, 0x01000193);
      b = Math.imul(b ^ byte, 0x01000193) ^ (b >>> 15);
    }
  }
  return (a >>> 0).toString(16).padStart(8, '0') + (b >>> 0).toString(16).padStart(8, '0');
}

export class InputRecorder {
  private inputs: PackedInput[] = [];
  private spawn: SpawnPoint | null = null;
  private key = '';

  get recording(): boolean {
    return this.spawn !== null;
  }

  get length(): number {
    return this.inputs.length;
  }

  /** Places the player at `spawn` and starts capturing. */
  begin(sim: Simulation, spawn: SpawnPoint): void {
    sim.placeAtSpawn(spawn);
    this.spawn = { position: [...spawn.position], yawDeg: spawn.yawDeg };
    this.key = sim.recordKey();
    this.inputs = [];
  }

  /** Call with the input of each tick, before stepping. Returns false once the length cap is hit. */
  capture(input: Readonly<InputFrame>): boolean {
    if (!this.spawn) return false;
    if (this.inputs.length >= MAX_RECORDING_TICKS) return false;
    this.inputs.push(packInput(input));
    return true;
  }

  /** Stops capturing; call after the last captured tick has been stepped. */
  end(sim: Simulation): Recording | null {
    const spawn = this.spawn;
    this.spawn = null;
    if (!spawn || this.inputs.length === 0) return null;
    return { version: 1, levelId: sim.level.data.id, recordKey: this.key, spawn, inputs: this.inputs, finalHash: stateHash(sim) };
  }
}

/** Validates an untrusted recording (e.g. pasted JSON). */
export function parseRecording(raw: unknown): Recording {
  const fail = (m: string): never => {
    throw new RecordingError(m);
  };
  if (typeof raw !== 'object' || raw === null) fail('Recording must be an object');
  const o = raw as Record<string, unknown>;
  if (o['version'] !== 1) fail('Unsupported recording version');
  if (typeof o['levelId'] !== 'string' || typeof o['recordKey'] !== 'string' || typeof o['finalHash'] !== 'string') fail('Recording header is incomplete');
  const spawn = o['spawn'] as Record<string, unknown> | undefined;
  const pos = spawn?.['position'];
  if (!Array.isArray(pos) || pos.length !== 3 || !pos.every((n) => typeof n === 'number' && Number.isFinite(n) && Math.abs(n) <= MAX_SPAWN_COORD) || typeof spawn?.['yawDeg'] !== 'number' || !Number.isFinite(spawn['yawDeg'])) {
    fail('Recording spawn is invalid');
  }
  const inputs = o['inputs'];
  if (!Array.isArray(inputs) || inputs.length === 0 || inputs.length > MAX_RECORDING_TICKS) fail(`Recording must have 1..${MAX_RECORDING_TICKS} inputs`);
  for (const p of inputs as unknown[]) {
    const ok =
      Array.isArray(p) &&
      p.length === 5 &&
      p.every((n) => typeof n === 'number' && Number.isFinite(n)) &&
      Math.abs(p[0] as number) <= 1 &&
      Math.abs(p[1] as number) <= 1 &&
      Number.isInteger(p[4]) &&
      (p[4] as number) >= 0 &&
      (p[4] as number) <= 7;
    if (!ok) fail('Recording contains an invalid input frame');
  }
  return {
    version: 1,
    levelId: o['levelId'] as string,
    recordKey: o['recordKey'] as string,
    spawn: { position: [...(pos as [number, number, number])], yawDeg: spawn!['yawDeg'] as number },
    inputs: (inputs as PackedInput[]).map((p) => [...p] as PackedInput),
    finalHash: o['finalHash'] as string,
  };
}

export type ReplayStatus = 'playing' | 'paused' | 'finished';

/**
 * Drives a simulation from a recording. The host calls `next()` for each tick
 * it wants to advance (respecting pause and slow-motion itself).
 */
export class ReplayPlayer {
  cursor = 0;
  status: ReplayStatus = 'paused';
  /** Null until finished; then whether the end state matched the recording. */
  matched: boolean | null = null;
  readonly configMatches: boolean;

  constructor(
    private readonly sim: Simulation,
    readonly recording: Recording,
  ) {
    if (recording.levelId !== sim.level.data.id) throw new RecordingError(`Recording is for level "${recording.levelId}", not "${sim.level.data.id}"`);
    this.configMatches = recording.recordKey === sim.recordKey();
    sim.placeAtSpawn(recording.spawn);
  }

  get total(): number {
    return this.recording.inputs.length;
  }

  /** Input for the next tick, or null when finished. Advances the cursor. */
  next(): InputFrame | null {
    if (this.cursor >= this.total) return null;
    return unpackInput(this.recording.inputs[this.cursor++]!);
  }

  /** Call after stepping the tick returned by next(). */
  afterStep(): void {
    if (this.cursor >= this.total && this.matched === null) {
      this.matched = stateHash(this.sim) === this.recording.finalHash;
      this.status = 'finished';
    }
  }

  /** Runs the whole recording synchronously (tests, verification). */
  runToEnd(): boolean {
    for (let f = this.next(); f; f = this.next()) {
      this.sim.step(f);
      this.afterStep();
    }
    return this.matched === true;
  }
}
